'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { useController } from '@lib/hooks/useController';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { useMemo, useState } from 'react';
import { InputV2, TreeAccordion } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { getPublisherController } from '../facetControllers';
import { StringFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { PublisherFacetProps } from './PublisherFacet.types';

const getValueId = (v: StringFlatFacetValue): string => v.value;

// The controller fetches a much larger value set so the search box can match
// the full publisher list client-side, but rendering it all at once is
// wasteful — while the search box is empty, only this many rows are mounted
// (plus any selected values beyond the cutoff).
const IDLE_RENDER_LIMIT = 20;

export const PublisherFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: PublisherFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  const publisherLabel =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.filter-publisher-label'
    )?.value ?? 'Publisher';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.clear')?.value ??
    'Clear';

  const searchPlaceholder =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.filter-publisher-placeholder'
    )?.value ?? 'Search publishers...';

  const [searchQuery, setSearchQuery] = useState('');

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getPublisherController,
    fieldName: 'publisher_name',
    getValueId,
  });

  // Current-response values, in Coveo's occurrences order for the active
  // scope. optimisticValues preserves engine-lifetime first-seen order, so
  // after a scope change its head can be stale zero-result rows — the idle
  // list must follow the current response instead.
  const eng = useEngine();
  const controller = useMemo(() => getPublisherController(eng), [eng]);
  const { values: currentValues } = useController(controller);

  const filteredValues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      const byValue = new Map(optimisticValues.map((v) => [v.value, v]));
      const top = currentValues
        .slice(0, IDLE_RENDER_LIMIT)
        .map((v) => byValue.get(v.value) ?? v);
      const inTop = new Set(top.map((v) => v.value));
      const selectedBeyondTop = optimisticValues.filter(
        (v) => v.state === 'selected' && !inTop.has(v.value)
      );
      return [...top, ...selectedBeyondTop];
    }
    return optimisticValues.filter((v) =>
      v.value.toLowerCase().includes(query)
    );
  }, [optimisticValues, currentValues, searchQuery]);

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      filteredValues.map((v) => ({
        id: getValueId(v),
        label: v.value,
        count: v.numberOfResults,
        disabled: v.numberOfResults === 0 && v.state !== 'selected',
      })),
    [filteredValues]
  );

  const content = (
    <div className="flex flex-col">
      <InputV2
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={searchPlaceholder}
        className="mb-1 w-full text-sm"
        aria-label={searchPlaceholder}
      />
      <div className="max-h-[200px] overflow-y-auto">
        <TreeAccordion
          items={treeItems}
          selectionMode="multiple"
          selectedIds={selectedIds}
          onSelectMany={handleSelectMany}
          isDarkTheme={isDark}
          size={variant === 'sidebar' ? 'default' : 'sm'}
          reserveExpandSpace={variant !== 'horizontal'}
        />
      </div>
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={publisherLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={publisherLabel}
      variant={variant}
      className={className}
      showClear={variant === 'drawer' && selectedCount > 0}
      onClear={handleClear}
      clearLabel={clearLabel}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
